import { FunctionComponent, useMemo } from 'react'

import { Icons } from 'atoms'
import { Button, HStack, IconButton, Menu, Text, VStack, useDisclose } from 'native-base'

type OpportunityDeletionJustificationSelectOption = {
  value: string
  label: string
}

type OpportunityDeletionJustificationSelectProps = {
  value?: string
  onChange: (value?: string) => void
  options: OpportunityDeletionJustificationSelectOption[]
  placeholder?: string
}

export const OpportunityDeletionJustificationSelect: FunctionComponent<
  OpportunityDeletionJustificationSelectProps
> = ({ value, onChange, options, placeholder = 'Selecione uma justificativa' }) => {
  const { isOpen, onOpen, onClose } = useDisclose(false)

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  )

  return (
    <Menu
      w="full"
      isOpen={isOpen}
      onClose={onClose}
      closeOnSelect
      placement={undefined}
      bgColor="white"
      borderRadius={20}
      trigger={(triggerProps) => {
        const triggerPropsWithoutOnPress = { ...triggerProps }

        delete triggerPropsWithoutOnPress.onPress

        return (
          <Button
            onPress={() => {
              if (isOpen) {
                onClose()
              } else {
                onOpen()
              }
            }}
            h="56px"
            w="full"
            justifyContent="space-between"
            px={4}
            bg="gray.30"
            borderRadius="full"
            colorScheme="white"
            _hover={{ bg: 'gray.30' }}
            _pressed={{ bg: 'gray.30' }}
            _focus={{ bg: 'gray.30', borderColor: 'gray.200' }}
            _stack={{
              w: 'full',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              space: 0
            }}
            rightIcon={
              <HStack alignItems="center" space={2}>
                {!!selectedOption && (
                  <IconButton
                    variant="unstyled"
                    onPress={(event) => {
                      event.stopPropagation()

                      onChange(undefined)
                    }}
                    icon={<Icons.CloseTab color="gray.700" size={4} />}
                    _focus={{ bg: 'transparent' }}
                    _hover={{ bg: 'transparent' }}
                    _pressed={{ opacity: 0.6 }}
                    minW="24px"
                    h="24px"
                    p={0}
                  />
                )}
                <Icons.ArrowDropDown color="gray.700" size={6} />
              </HStack>
            }
            {...triggerPropsWithoutOnPress}>
            <Text
              flex={1}
              textAlign="left"
              fontSize="16px"
              lineHeight="24px"
              fontWeight={selectedOption ? 500 : 400}
              color={selectedOption ? 'gray.700' : 'gray.400'}>
              {selectedOption ? selectedOption.label : placeholder}
            </Text>
          </Button>
        )
      }}
      _backdrop={{ bg: 'transparent' }}
      //@ts-ignore
      _presence={{
        entryDuration: 0
      }}
      _content={{
        bg: 'white',
        borderRadius: 20,
        py: 2,
        px: 0,
        shadow: 2,
        minW: '100%',
        w: '100%'
      }}>
      <VStack space={0} py={2} w="full">
        {options.map((option, index) => {
          const isSelected = option.value === selectedOption?.value

          return (
            <Menu.Item
              key={option.value}
              px={8}
              py={3}
              w="100%"
              backgroundColor={isSelected ? 'gray.50' : 'white'}
              borderBottomWidth="1px"
              borderTopWidth={index === 0 ? '1px' : '0'}
              borderColor="gray.50"
              _pressed={{ backgroundColor: 'gray.30' }}
              _hover={{ backgroundColor: 'gray.30' }}
              onPress={() => {
                onChange(option.value)

                onClose()
              }}>
              <Text fontSize="16px" lineHeight="24px" color="gray.700">
                {option.label}
              </Text>
            </Menu.Item>
          )
        })}
      </VStack>
    </Menu>
  )
}
